/**
 * PaymentsTab — the WIRED reservation payments tab body (SCRUM-338). A
 * `modules/booking` feature: a thin renderer over the pure `toPaymentsView`
 * projection plus the existing `useReservationFolio` (for the header badge). It
 * composes the kit `Badge` / `Button` primitives and a semantic HTML `<table>`
 * (there is no kit `Table` primitive; the panel already renders inside the
 * detail's `Card` chrome, so no nested Card here) — no new primitives, no barrel
 * widening (per plan / LedgerTab precedent).
 *
 * **Two independent permission regions (plan "two permission regions").**
 *   - Region A — the header badge (Paid-in-full / $X-due), from
 *     `useReservationFolio` (`payments.folios.read`, which also yields the
 *     `folioId`). A folio 403 → the WHOLE tab is an honest partial ("needs
 *     additional access") — with no `folioId` there is nothing to fetch. A folio
 *     404 → honest-empty pre-confirm (no folio yet), NOT an error (no Retry).
 *   - Region B — the payment log, from `useFolioPayments(folioId)`
 *     (`payments.payments.read`). A payments 403 → the badge still shows + the
 *     log region is an honest partial. Empty `{data:[]}` → "No payments recorded
 *     yet". Gateway rows ride this SAME log via `processingRail:'gateway'`.
 *
 * **Money is `Cents` (CLAUDE.md rule 6 / FE rule §6).** `balanceDueCents` and
 * each row's `amountCents` are server passthroughs; `formatCents(cents, intl)`
 * runs at the display boundary only (never a hand-rolled `/100`, never FE
 * arithmetic). A `null` processor / gateway ref renders the neutral "—" marker.
 *
 * **Time.** Payment `occurredAt` is an ISO 8601 string → `intl.formatDate` (NOT
 * the entity-change epoch-ms formatter, which the history tab uses).
 *
 * **A11y (WCAG 2.1 AA, FE rule 7).** Semantic `<table>` + `<caption>` +
 * `<th scope="col">`; direction + status conveyed by TEXT never color alone;
 * `aria-live` on the error region; axe-clean in light + dark.
 *
 * **Tokens (ADR frontend-0002/0003, SCRUM-78/79).** Semantic `var(--token)` via
 * arbitrary-value classes only; no raw hex/rgba, no inline-style numeric
 * literals, no pixel-literal arbitrary values.
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { Badge, Button } from '@sparta/ui-kit';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';
import { useReservationFolio } from '../api/use-reservation-folio.js';
import { useFolioPayments } from '../api/use-folio-payments.js';
import { toPaymentsView, type PaymentView } from '../api/to-payments-view.js';

export interface PaymentsTabProps {
  /** The reservation whose payments to render (`/reservations/:id`). */
  readonly reservationId: string;
}

/** The neutral marker for a null / absent optional field. */
const DASH = '—';

export function PaymentsTab({ reservationId }: PaymentsTabProps): React.ReactElement {
  const folio = useReservationFolio(reservationId);

  // ── loading: the folio (Region A) is still resolving ─────────────────────
  if (folio.status === 'loading') {
    return <PaymentsSkeleton />;
  }

  // ── folio forbidden: no folioId → whole-tab honest partial (no log fetch) ─
  if (folio.status === 'forbidden') {
    return <WholeTabForbidden />;
  }

  // ── folio error: a genuine folio failure blanks the tab (Retry) ──────────
  if (folio.status === 'error') {
    return <PaymentsError onRetry={folio.refetch} />;
  }

  // ── folio 404: honest-empty pre-confirm (no folio yet), NOT an error ─────
  if (folio.status === 'notFound') {
    return <PreConfirmEmpty />;
  }

  // folio.status === 'ready' — we have the folioId + balanceDueCents.
  return <PaymentsBody folioId={folio.data.folioId} balanceDueCents={folio.data.balanceDueCents} />;
}

/* ── body: header badge (Region A) + payment log (Region B) ───────────────── */

function PaymentsBody({
  folioId,
  balanceDueCents,
}: {
  readonly folioId: string;
  readonly balanceDueCents: number;
}): React.ReactElement {
  const payments = useFolioPayments(folioId);

  return (
    <div className="flex flex-col gap-4">
      {/* "Payment log" heading + balance badge on one bordered header row
          (design reservations.jsx:249-251) — badge inline-right, not stacked. */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-subtle)] pb-3">
        <h2 className="m-0 text-base font-bold text-[color:var(--fg-primary)]">Payment log</h2>
        <BalanceBadge balanceDueCents={balanceDueCents} />
      </div>
      <PaymentLogRegion payments={payments} />
    </div>
  );
}

/* ── header badge: Paid in full / $X due (text-carried, never color alone) ── */

function BalanceBadge({
  balanceDueCents,
}: {
  readonly balanceDueCents: number;
}): React.ReactElement {
  const intl = useIntl();
  // `balanceDueCents` is DERIVED server-side (`totalDue − netCollected`) and is
  // NOT clamped at zero: a downward re-freeze after collection (a modified stay,
  // a cancellation credit) can drive it NEGATIVE — a real overpaid / credit
  // state. A two-way "0 vs due" split would then render fabricated debt
  // ("-$20.00 due") for a credit. Branch on the sign and tell the truth for each.
  // Status is conveyed by TEXT — the label spells out the state; the variant
  // (color) is redundant reinforcement only (WCAG 1.4.1). The amount comes from
  // the server Cents via formatCents (never a hand-rolled /100), signed by Intl.
  let label: string;
  let variant: 'success' | 'warning' | 'neutral';
  if (balanceDueCents === 0) {
    label = 'Paid in full';
    variant = 'success';
  } else if (balanceDueCents > 0) {
    label = `${formatCents(balanceDueCents, intl)} due`;
    variant = 'warning';
  } else {
    // Overpaid: the folio holds a guest credit. Show the (Intl-signed) balance
    // with honest credit copy — never "$X due".
    label = `${formatCents(balanceDueCents, intl)} credit`;
    variant = 'neutral';
  }
  return (
    <div data-testid="balance-badge">
      <Badge variant={variant}>{label}</Badge>
    </div>
  );
}

/* ── Region B: the payment log (its own permission gate) ──────────────────── */

function PaymentLogRegion({
  payments,
}: {
  readonly payments: ReturnType<typeof useFolioPayments>;
}): React.ReactElement {
  if (payments.status === 'loading') {
    return <PaymentLogSkeleton />;
  }
  if (payments.status === 'forbidden') {
    return <LogForbidden />;
  }
  if (payments.status === 'error') {
    return <PaymentsError onRetry={payments.refetch} />;
  }

  // payments.status === 'ready'
  const rows = toPaymentsView(payments.data);
  if (rows.length === 0) {
    return <LogEmpty />;
  }
  return <PaymentLogTable rows={rows} />;
}

/* ── the payment log table (semantic, a11y) ───────────────────────────────── */

function PaymentLogTable({ rows }: { readonly rows: PaymentView[] }): React.ReactElement {
  const intl = useIntl();
  return (
    <section aria-label="Payment log">
      <div className="max-w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          {/* Visible heading now lives in the header row above; keep an sr-only
              caption so the table still carries an accessible name. */}
          <caption className="sr-only">Payment log</caption>
          {/* Column order mirrors the design (date · reference · method ·
              processor · amount); Status is a wire-backed enrichment at the end.
              On mobile only Date + Amount remain (the design's compact collapse);
              ref/method/processor/status are `hidden sm:table-cell`. */}
          <thead>
            <tr className="border-b border-[var(--border-subtle)] text-left">
              <th scope="col" className="py-2 pr-4 font-semibold text-[color:var(--fg-secondary)]">
                Date
              </th>
              <th
                scope="col"
                className="hidden py-2 pr-4 font-semibold text-[color:var(--fg-secondary)] sm:table-cell"
              >
                Reference
              </th>
              <th
                scope="col"
                className="hidden py-2 pr-4 font-semibold text-[color:var(--fg-secondary)] sm:table-cell"
              >
                Method
              </th>
              <th
                scope="col"
                className="hidden py-2 pr-4 font-semibold text-[color:var(--fg-secondary)] sm:table-cell"
              >
                Processor
              </th>
              <th
                scope="col"
                className="py-2 pr-4 text-right font-semibold text-[color:var(--fg-secondary)]"
              >
                Amount
              </th>
              <th
                scope="col"
                className="hidden py-2 font-semibold text-[color:var(--fg-secondary)] sm:table-cell"
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.paymentId}
                className="border-b border-[var(--border-subtle)] text-[color:var(--fg-primary)]"
              >
                <td className="py-2 pr-4 tabular-nums">
                  {intl.formatDate(row.occurredAt, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
                <td className="hidden py-2 pr-4 sm:table-cell">{row.gatewayRef ?? DASH}</td>
                <td className="hidden py-2 pr-4 sm:table-cell">{row.methodLabel}</td>
                <td className="hidden py-2 pr-4 sm:table-cell">{row.processorName ?? DASH}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {/* Direction is conveyed by TEXT (never color alone, WCAG 1.4.1):
                      the amount is prefixed with the charge / refund label. */}
                  <span className="text-[color:var(--fg-tertiary)]">{row.directionLabel}</span>{' '}
                  <span className="font-semibold">{formatCents(row.amountCents, intl)}</span>
                </td>
                <td className="hidden py-2 sm:table-cell">{row.statusLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── loading skeleton ─────────────────────────────────────────────────────── */

function PaymentsSkeleton(): React.ReactElement {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="skeleton">
      <span className="sr-only">Loading payments…</span>
      <div className="flex flex-col gap-3">
        <span className="inline-block h-6 w-40 rounded bg-[var(--bg-sunken)]" aria-hidden="true" />
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="inline-block h-5 w-full rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

function PaymentLogSkeleton(): React.ReactElement {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="skeleton">
      <span className="sr-only">Loading payment log…</span>
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block h-5 w-full rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

/* ── error + retry ────────────────────────────────────────────────────────── */

function PaymentsError({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <div role="alert" aria-live="assertive" className="flex items-center justify-between gap-4">
      <div>
        <h3 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
          Couldn’t load payments.
        </h3>
        <p className="text-sm text-[color:var(--fg-secondary)]">
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/* ── honest partial: folio forbidden (whole tab) ──────────────────────────── */

function WholeTabForbidden(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <h3 className="text-sm font-semibold text-[color:var(--fg-primary)]">
        Payments needs additional access
      </h3>
      <p className="mt-1 max-w-sm text-sm text-[color:var(--fg-secondary)]">
        You don’t have permission to view this reservation’s payment summary.
      </p>
    </div>
  );
}

/* ── honest partial: payments-log forbidden (Region B only) ───────────────── */

function LogForbidden(): React.ReactElement {
  return (
    <section
      aria-labelledby="payments-log-heading"
      className="border-t border-[var(--border-subtle)] pt-3"
    >
      <h3
        id="payments-log-heading"
        className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
      >
        Payment log
      </h3>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        The payment log needs additional access.
      </p>
    </section>
  );
}

/* ── honest-empty: pre-confirm (no folio yet) ─────────────────────────────── */

function PreConfirmEmpty(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <h3 className="text-sm font-semibold text-[color:var(--fg-primary)]">No payments yet</h3>
      <p className="mt-1 max-w-sm text-sm text-[color:var(--fg-secondary)]">
        This reservation isn’t confirmed, so there’s no payment folio yet.
      </p>
    </div>
  );
}

/* ── honest-empty: confirmed folio with no payments recorded ──────────────── */

function LogEmpty(): React.ReactElement {
  return (
    <section
      aria-labelledby="payments-log-heading"
      className="border-t border-[var(--border-subtle)] pt-3"
    >
      <h3
        id="payments-log-heading"
        className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
      >
        Payment log
      </h3>
      <p className="text-sm text-[color:var(--fg-secondary)]">No payments recorded yet.</p>
    </section>
  );
}
